// Facebook (Meta) constants for the CLW / Launch-details review grid.
//
// Pure data — no React, no I/O — mirroring gads/gadsConstants.js and
// ttd/ttdConstants.js so the review surfaces can pick a column set / editable
// set / editor map without JSX branching.
//
// Facebook's hierarchy is campaign → ad set → creative
// (campaign-services/campaign_launcher/platform_config.py, PLATFORM_HIERARCHIES
// "fb"), so ONE GRID ROW IS ONE AD SET — the package-equivalent level, the same
// place TTD puts an ad group and DV360 a line item. Creatives hang off the ad
// set and are summarised in the creative columns rather than getting rows of
// their own.

import { editableFieldsFor } from '../clwGridConstants';
import { scaleToParent } from '../sharedFieldWriters';

// ── Platform identity ────────────────────────────────────────────────
// Every spelling a Facebook plan's platform id arrives in.
// `launcherResult.platform` uses the launcher's mapped key ('fb');
// `campaigns.destination` uses the create-form channel id ('facebook'); the
// handler's PLATFORM_MAPPING also accepts 'meta' and 'fb_api'
// (campaign-services/handlers/campaign_launcher_handler.py).
const FB_PLATFORM_TOKENS = new Set(['fb', 'facebook', 'meta', 'fb_api', 'fb-api', 'facebook_ads', 'meta_ads']);

/** True when `platform` names Facebook, in any of its spellings. */
export const isFbPlatform = (platform) => FB_PLATFORM_TOKENS.has(
  String(platform || '').trim().toLowerCase(),
);

export const FB_PLATFORM_LABEL = 'Facebook';
// Short format-pill code — the `fmt` field on every row.
export const FB_FMT = 'META';
// What one row represents, for the grid footer / QA table headers.
export const FB_ROW_LABEL = 'ad sets';

// ── Column groups ────────────────────────────────────────────────────
// The same seven tabs DV360 / TTD / Google Ads use, so the grid's tab bar is
// identical across platforms; the columns inside are Facebook-native. Keys map
// to the fields buildFbReviewRows() writes.
export const FB_GROUP_COLS = {
  overview: [
    { k: 'fmt', l: 'Type', w: 76 },
    { k: 'package', l: 'Package', w: 170 },
    { k: 'tactic', l: 'Tactic', w: 130 },
    { k: 'objective', l: 'Objective', w: 180 },
    { k: 'start', l: 'Start', w: 96, fmt: 'date' },
    { k: 'end', l: 'End', w: 96, fmt: 'date' },
    { k: 'budget', l: 'Budget', w: 110, fmt: 'cur' },
  ],
  flighting: [
    { k: 'start', l: 'Start', w: 110, fmt: 'date' },
    { k: 'end', l: 'End', w: 110, fmt: 'date' },
    { k: 'flightType', l: 'Flight type', w: 160 },
    { k: 'dailyBudget', l: 'Daily budget', w: 120, fmt: 'cur' },
    { k: 'pacing', l: 'Budget type', w: 140 },
    { k: 'entityStatus', l: 'Status', w: 100 },
  ],
  budget: [
    { k: 'package', l: 'Package', w: 170 },
    { k: 'budget', l: 'Ad set budget', w: 120, fmt: 'cur' },
    { k: 'dailyBudget', l: 'Daily budget', w: 120, fmt: 'cur' },
    { k: 'campaignBudget', l: 'Campaign budget', w: 140, fmt: 'cur' },
    { k: 'pacing', l: 'Budget type', w: 140 },
    { k: 'bidFloor', l: 'Bid cap', w: 100 },
  ],
  targeting: [
    { k: 'geo', l: 'Geography', w: 220 },
    { k: 'zipcodes', l: 'Zip codes', w: 130 },
    { k: 'audiences', l: 'Custom audiences', w: 180 },
    { k: 'interests', l: 'Interests', w: 160 },
    { k: 'age', l: 'Age', w: 100 },
    { k: 'gender', l: 'Gender', w: 110 },
    { k: 'languages', l: 'Languages', w: 130 },
    { k: 'devices', l: 'Devices', w: 150 },
    { k: 'placements', l: 'Placements', w: 200 },
  ],
  creative: [
    { k: 'creative', l: 'Creatives', w: 120 },
    { k: 'creativeFormat', l: 'Formats', w: 180 },
    { k: 'adCopy', l: 'Ad copy', w: 130 },
    { k: 'clickURL', l: 'Landing page', w: 260 },
  ],
  bid: [
    { k: 'bidStrategy', l: 'Bid strategy', w: 210 },
    { k: 'bidFloor', l: 'Bid cap', w: 100 },
    { k: 'optimizationGoal', l: 'Optimization goal', w: 180 },
    { k: 'billingEvent', l: 'Billing event', w: 150 },
  ],
  tracking: [
    { k: 'objective', l: 'Objective', w: 180 },
    { k: 'attribution', l: 'Attribution', w: 150 },
    { k: 'optimizationGoal', l: 'Optimization goal', w: 180 },
    { k: 'clickURL', l: 'Landing page', w: 280 },
  ],
};
export const FB_GROUP_KEYS = Object.keys(FB_GROUP_COLS);

// ── Editable set ─────────────────────────────────────────────────────
// EDITABLE BY DEFAULT, like every other platform (see clwGridConstants.js):
// each column is editable unless the denylist names it, and fb/fbEdits.js
// carries a writer for every scalar in the derived set.
//
// Campaign-scoped cells (objective / attribution / campaign budget) are
// editable and their write lands on the campaign, so every sibling row
// re-reads the new value — the same contract Google Ads uses for its
// campaign-level cells. That is Facebook's entity model, not a leak.
// Reason map doubles as the denylist — keys gate the grid, values are shown
// to the user in the cell detail panel (see clwGridConstants.js).
export const FB_READ_ONLY_REASONS = {
  entityStatus: "Activation is the launch action's job — a review grid must never promote an entity into a live state.",
  fmt: "A static 'META' row tag — no storage of its own.",
  flightType: "A derived label — an ad set's flight IS the Start/End cells; edit those.",
  creative: 'A creative count — creatives are managed by upload, not typed into a cell.',
  creativeFormat: "Derived from each creative's format — edit the creative, not the summary.",
};
export const FB_DENYLIST = new Set(Object.keys(FB_READ_ONLY_REASONS));

export const FB_EDITABLE = editableFieldsFor(FB_GROUP_COLS, FB_DENYLIST);

// ── Parent-scaled cells (pre-commit disclosure) ──────────────────────
// The Facebook equivalent of TTD_SCALED_FIELDS / GADS_SCALED_FIELDS — see the
// long note in ../clwGridConstants.js.
//
// Only `campaignBudget` qualifies. The ad set's own `budget` / `dailyBudget`
// cells are stored ON the ad set (Facebook ABO), so they write exactly what is
// typed and have no hidden total; the campaign budget cell shows the campaign
// total's PER-AD-SET share, so committing it writes share × ad sets.
export const FB_SCALED_FIELDS = {
  campaignBudget: {
    scope: 'campaign budget',
    unit: 'currency',
    sibNoun: 'ad set',
    total: (share, row) => scaleToParent(share, row?.siblingCount),
  },
};

// ── List-shaped cells ────────────────────────────────────────────────
// Same shape as TTD_LIST_FIELDS / DV360_LIST_FIELDS / GADS_LIST_FIELDS: per
// field, which of add / edit / remove the cell detail panel offers plus the add
// input's placeholder. Geo stages ops against geoInclude / geoExclude, the two
// sub-fields the panel's deletion flow uses everywhere.
//
// Everything listed is writable by fb/fbEdits.js#applyFbEdits against the key
// buildFbReviewRows renders.
export const FB_LIST_FIELDS = {
  // Facebook geo_locations hold countries / regions / cities / geo_markets
  // (DMA codes) / zips. Adds route by what was typed — see fbEdits.applyFbGeoAdd
  // — and the launcher resolves names through fb_targeting_lookup at launch, so
  // a typed market name is as launchable as a parsed one.
  geoInclude: {
    ops: { add: true, edit: true, remove: true },
    addPlaceholder: 'Add country, DMA or city…',
    note: 'A 2-letter code targets a country, "DMA 501" a designated market area; anything else is resolved by name at launch',
  },
  geoExclude: {
    ops: { add: true, edit: true, remove: true },
    addPlaceholder: 'Add location to exclude…',
  },
  // Facebook zips must carry their country prefix (US:94002) — the validator
  // rejects a bare zip, so the placeholder states the format rather than
  // letting the user find out at launch.
  zipcodes: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add zip (e.g. US:94002)…' },
  // Custom audiences carry Meta ids; a name-only entry is resolved by the
  // launcher, so typed adds are safe.
  audiences: {
    ops: { add: true, edit: true, remove: true },
    addPlaceholder: 'Add custom audience name or ID…',
    note: 'Audiences without an ID are resolved by name at launch',
  },
  // Detailed-targeting interests — same story, resolved through the Meta
  // targeting search at launch.
  interests: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add interest…' },
  // publisher_platforms + facebook_positions, unioned into one cell.
  placements: {
    ops: { add: true, edit: true, remove: true },
    addPlaceholder: 'Add placement…',
    note: 'A known platform (facebook, instagram, messenger, audience_network) sets publisher_platforms; anything else is a position',
  },
  devices: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add device (mobile / desktop)…' },
  languages: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add locale…' },
  // Per-creative primary text. Removing an entry does NOT delete the creative
  // — it clears that creative's copy; the creative itself is managed by upload.
  adCopy: { ops: { add: false, edit: true, remove: false }, reason: 'Creatives are added by upload — this edits the copy on the ones already in the plan' },
};

// Editor type per column — 'currency' strips $ / commas on commit, 'date' uses
// a native date input, anything unlisted edits as free text.
export const FB_FIELD_EDITORS = {
  budget: 'currency',
  dailyBudget: 'currency',
  campaignBudget: 'currency',
  bidFloor: 'currency',
  start: 'date',
  end: 'date',
};
